import { useState, useEffect, useRef } from "react";

import { ImpressionsChart } from "../../components/charts3";
import { ChartControls } from "../../components/Channel";

import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useDateInputs } from "../../hooks/useDateInputs";
import { useSwitchInSeries } from "../../hooks/useSwitchInSeries";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";



export default function ChannelSwitchIn({ channelId, rawImpressions, loading, refreshKey,refreshing, refreshEnabled, setRefreshEnabled, refreshIntervalMin, setRefreshIntervalMin, refreshCountdown, manualRefresh }) {
  const defaultMode = "hour";
  
  const [mode, setMode] = useState(defaultMode);

  // --- LOGGER INTEGRATION ---
  const { logPageChange, logTimeframeChange } = useUserJourneyLogger({
    channelId,
    // channelName is not available here, so omit or set to undefined
  });

  useEffect(() => {
    logPageChange({
      chartType: "switchin",
    });
  }, [logPageChange]);

  // Only log timeframe change on user-initiated mode change
  const handleModeChange = (newMode) => {
    if (newMode !== "hour") return;

    setMode("hour");

    logTimeframeChange({
      chartType: "switchin",
      mode: "hour",
    });
  };

  // Time range (minute only – harmless for others)
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  // rawImpressions and loading are now received as props
  const sortedKeys = useDaysSorted(rawImpressions);

  // ✅ SINGLE source of truth for dates (YYYY-MM-DD)
  const dates = useDateInputs(sortedKeys, mode);

  /**
   * ✅ Minute mode default date
   * - Only once per entry into minute mode
   * - Never override user selection
   */
  const minuteInitRef = useRef(false);

  useEffect(() => {
    if (mode !== "minute") return;
    if (!sortedKeys?.length) return;
    if (minuteInitRef.current) return;

    if (dates.startDateInput && dates.endDateInput) {
      minuteInitRef.current = true;
      return;
    }

    const latestKey = sortedKeys.at(-1); // DD-MM-YYYY
    const [d, m, y] = latestKey.split("-");
    const uiDate = `${y}-${m}-${d}`; // YYYY-MM-DD

    dates.setStartDateInput(uiDate);
    dates.setEndDateInput(uiDate);

    minuteInitRef.current = true;
  }, [mode, sortedKeys, dates]);

  // Reset minute init when leaving minute
  useEffect(() => {
    if (mode !== "minute") {
      minuteInitRef.current = false;
    }
  }, [mode]);

  const series = useSwitchInSeries({
    rawImpressions,
    sortedImpKeys: sortedKeys,
    startDateInput: dates.startDateInput,
    endDateInput: dates.endDateInput,
    mode,
    startTime,
    endTime,
  });

  const showChart = !loading;

  return (
    <>
      <ChartControls
        channelId={channelId}
        mode={mode}
        onModeChange={handleModeChange}
        allowedModes={[]}
        startDateInput={dates.startDateInput}
        endDateInput={dates.endDateInput}
        onStartDateChange={dates.setStartDateInput}
        onEndDateChange={dates.setEndDateInput}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
      />

      {showChart ? (
        <ImpressionsChart
          series={series}
          mode={mode}
          startTime={startTime}
          endTime={endTime}
          refreshKey={refreshKey}
        />  
        ) : (
        <div className="chart-container">
          <h3 className="chart-title">Switch-In (Impressions)</h3>
          <div className="chart-loading">
            <div className="chart-skeleton" />
            <div className="chart-loading-text">Loading chart...</div>
          </div>
        </div>
      )}
    </>
  );
}
